import React, { useEffect, useState, useMemo } from "react";
import Header from "../header/Header";
import Statistics from "./Statistics";
import PieAndBarChart from "./PieAndBarChart";
import WorldMap from "./worldMap";
import DashboardLayout from "../layout";
import { useQuery } from "@tanstack/react-query";
import { getQuotes, getUserQuoteById } from "../../lib/quotes";
import { useMyContext } from "../../context/MyContext";
import Loader from "../shared/loader/loader";
import WelcomeTour from './WelcomeTour';

const Dashboard = () => {
  const [filteredData, setFilteredData] = useState([]);
  const [orderRecord, setOrderRecord] = useState([]);
  const [barRecord, setBarRecord] = useState([]);
  const { user, preloadedCountries, countriesLoading } = useMyContext();
  const [showLoader, setShowLoader] = useState(true);

  const {
    data,
    isLoading,
    isError,
    error,
    refetch
  } = useQuery(
    ["quotes", user?.uid],
    async () => {
      if (!user) {
        throw new Error("User data not available");
      }
      const result = user.role === "user" ? await getUserQuoteById(user.uid) : await getQuotes();
      return result;
    },
    {
      staleTime: 5 * 60 * 1000, // 5min stale
      cacheTime: 10 * 60 * 1000, // 10min cache
      refetchOnWindowFocus: false,
      retry: 2, // Retry twice automatically before showing error
      enabled: !!user,
      onError: (error) => {
        console.error("Error fetching quotes:", error);
      }
    }
  );

  const { data: dataForGraph, isError: isGraphError, error: graphError } = useQuery(
    ["quotesGraph", user?.uid],
    async () => {
      if (!user) return [];
      const result = user.role === "user" ? await getUserQuoteById(user.uid) : await getQuotes();
      return result;
    },
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 2,
      enabled: !!user,
      onError: (error) => {
        console.error("Error fetching graph data:", error);
      }
    }
  );

  // Helper functions (moved BEFORE useMemo to avoid hoisting issue)
  const processAndLoadCountries = (quotes) => {
    if (!quotes || !Array.isArray(quotes)) {
      console.error("Invalid quotes data");
      return [["Country", "Shipments"]];
    }
    const countryOperations = {};

    quotes.forEach((quote) => {
      const { loadCountry, unloadCountry } = quote;

      if (loadCountry) {
        if (!countryOperations[loadCountry]) {
          countryOperations[loadCountry] = { load: 0, unload: 0 };
        }
        countryOperations[loadCountry].load += 1;
      }

      if (unloadCountry) {
        if (!countryOperations[unloadCountry]) {
          countryOperations[unloadCountry] = { load: 0, unload: 0 };
        }
        countryOperations[unloadCountry].unload += 1;
      }
    });

    const result = [["Country", "Shipments"]];
    Object.entries(countryOperations).forEach(([country, operations]) => {
      const totalOperations = operations.load + operations.unload;
      result.push([country, totalOperations]);
    });

    return result;
  };

  const getOrderStatusCounts = (quotes) => {
    const statusCounts = {
      completed: 0,
      canceled: 0,
      pending: 0,
      progress: 0,
    };

    quotes.forEach((order) => {
      const status = order.status;

      if (status === "completed") {
        statusCounts.completed++;
      } else if (status === "canceled") {
        statusCounts.canceled++;
      } else if (status === "pending") {
        statusCounts.pending++;
      } else if (status === "in progress") {
        statusCounts.progress++;
      }
    });

    return [
      ["Order", "Order per status"],
      ["Pending", statusCounts.pending],
      ["Cancel", statusCounts.canceled],
      ["Progress", statusCounts.progress],
      ["Complete", statusCounts.completed],
    ];

  };

  const processOrdersByWeek = (quotes) => {
    if (!quotes || !Array.isArray(quotes)) {
      return [];
    }

    const now = new Date();
    const threeMonthsAgo = new Date(now);
    threeMonthsAgo.setMonth(now.getMonth() - 3);

    // Group quotes by week
    const weeklyData = {};

    quotes.forEach((quote) => {
      const { creationDate } = quote;

      if (creationDate) {
        const date = new Date(creationDate);

        // Only include dates within the last 3 months
        if (date >= threeMonthsAgo && date <= now) {
          // Get the week start date (Sunday)
          const weekStart = new Date(date);
          weekStart.setDate(date.getDate() - date.getDay());
          const weekKey = weekStart.toISOString().split('T')[0];

          // Format for display: "MMM DD" (e.g., "Apr 10")
          const month = weekStart.toLocaleString('en-US', { month: 'short' });
          const day = weekStart.getDate();
          const displayWeek = `${month} ${day}`;

          if (!weeklyData[weekKey]) {
            weeklyData[weekKey] = {
              week: displayWeek, // Using formatted date for display
              orders: 0,
              orders: 0,
              rawDate: weekStart, // Store actual date for sorting
            };
          }

          weeklyData[weekKey].orders += 1;
        }
      }
    });

    return Object.values(weeklyData).sort((a, b) => a.rawDate - b.rawDate);
  };

  // Memoized data processing ( after helpers)
  const processedData = useMemo(() => {
    if (!data || !dataForGraph) return { countries: null, orderRecord: [], barRecord: [] };

    const completedOrDelivered = data.filter(
      (item) => item.status === "completed" || item.status === "delivered"
    );
    setFilteredData(completedOrDelivered);

    const countryResult = processAndLoadCountries(dataForGraph);
    const orderResult = getOrderStatusCounts(dataForGraph);
    const barResult = processOrdersByWeek(dataForGraph);

    return {
      countries: countryResult,
      orderRecord: orderResult,
      barRecord: barResult,
    };
  }, [data, dataForGraph]);

  // Set processed data
  useEffect(() => {
    if (processedData.countries) setOrderRecord(processedData.orderRecord);
    if (processedData.barRecord) setBarRecord(processedData.barRecord);
  }, [processedData]);

  // Optimized loader: Hide immediately if ready, else short fallback
  useEffect(() => {
    if (data && dataForGraph && !countriesLoading) {
      setShowLoader(false);
    } else {
      const timer = setTimeout(() => setShowLoader(false), 500);
      return () => clearTimeout(timer);
    }
  }, [data, dataForGraph, countriesLoading]);

  const totalShipments = data?.length ?? 0;
  const ongoingShipments = totalShipments - (filteredData?.length ?? 0);
  const deliveredShipments = filteredData?.length ?? 0;

  const now = new Date();
  const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);

  const lastMonthShipments = data?.filter(shipment => {
    const creationDate = new Date(shipment.creationDate);
    return creationDate >= startOfLastMonth && creationDate <= endOfLastMonth;
  })?.length ?? 0;


  // Enhanced error handling with retry functionality
  if (isError || isGraphError) {
    const actualError = error || graphError;
    return (
      <DashboardLayout>
        <Header title="Dashboard" />
        <div className="flex flex-col items-center justify-center h-screen bg-[#eaeff6] p-5">
          <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Data Loading Error</h2>
            <p className="text-gray-700 mb-6">
              {actualError?.message || "There was a problem loading the dashboard data."}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => refetch()}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Retry Loading Data
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (data && data.length === 0) {
    return (
      <DashboardLayout>
        <Header title="Dashboard" />
        {showLoader ? (
          <Loader />
        ) : (
          <div className="p-5 bg-[#eaeff6]">
            <Statistics
              totalShipments={totalShipments}
              ongoingShipments={ongoingShipments}
              deliveredShipments={deliveredShipments}
              lastMonthShipments={lastMonthShipments}
            />
            {processedData.countries ? <WorldMap data={processedData.countries} /> : <Loader />}
            <PieAndBarChart orderRecord1={orderRecord} barRecord1={barRecord} />
          </div>
        )}
        <WelcomeTour />
      </DashboardLayout>
    );
  }

  const twoMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, 1);
  const endOfTwoMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 1, 0);

  const previousTotalShipments = data?.filter(shipment =>
    new Date(shipment.creationDate) <= endOfTwoMonthsAgo
  )?.length ?? undefined;

  const previousFilteredData = data?.filter(
    item => (item.status === "completed" || item.status === "delivered") &&
      new Date(item.creationDate) <= endOfTwoMonthsAgo
  ) ?? [];

  const previousOngoingShipments = previousTotalShipments !== undefined ?
    previousTotalShipments - previousFilteredData.length :
    undefined;

  const previousDeliveredShipments = previousFilteredData.length || undefined;

  const previousMonthShipments = data?.filter(shipment => {
    const creationDate = new Date(shipment.creationDate);
    return creationDate >= twoMonthsAgo && creationDate <= endOfTwoMonthsAgo;
  })?.length ?? undefined;

  if (isLoading || countriesLoading) {
    <DashboardLayout>
      <Header title="Dashboard" />
      {showLoader ? (
        <Loader />
      ) : (
        <div className="p-5 bg-[#eaeff6] min-h-screen">
          <Statistics
            totalShipments={totalShipments}
            ongoingShipments={ongoingShipments}
            deliveredShipments={deliveredShipments}
            lastMonthShipments={lastMonthShipments}
            previousTotalShipments={previousTotalShipments}
            previousOngoingShipments={previousOngoingShipments}
            previousDeliveredShipments={previousDeliveredShipments}
            previousLastMonthShipments={previousMonthShipments}
          />
          {processedData.countries ? <WorldMap data={processedData.countries} /> : <Loader />}
          <PieAndBarChart orderRecord1={orderRecord} barRecord1={barRecord} />
        </div>
      )}
      <WelcomeTour />
    </DashboardLayout>
  }

  return (
    <DashboardLayout>
      <Header title="Dashboard" />
      {showLoader ? (
        <Loader />
      ) : (
        <div className="p-5 bg-[#eaeff6] min-h-screen">
          <Statistics
            totalShipments={totalShipments}
            ongoingShipments={ongoingShipments}
            deliveredShipments={deliveredShipments}
            lastMonthShipments={lastMonthShipments}
            previousTotalShipments={previousTotalShipments}
            previousOngoingShipments={previousOngoingShipments}
            previousDeliveredShipments={previousDeliveredShipments}
            previousLastMonthShipments={previousMonthShipments}
          />
          {processedData.countries ? <WorldMap data={processedData.countries} /> : <Loader />}
          <PieAndBarChart orderRecord1={orderRecord} barRecord1={barRecord} />
        </div>
      )}
      <WelcomeTour />
    </DashboardLayout>
  );
};

export default Dashboard;